<template>
  <div class="overwrite-dialog">
    <p>Are you sure you want to overwrite this file?</p>
    <div class="file-name">"{{ uploadTarget.title }}"</div>
    <div class="dialog-footer">
      <Button label="Ok" icon="pi pi-check" @click="saveFiles"/>
      <Button label="Cancel" icon="pi pi-times" class="p-button-secondary" @click="cancelSaveAs"/>
    </div>
  </div>

</template>

<script setup>
import {computed, defineEmits, onMounted, ref} from "vue";
import {useMainStore, useSlidesStore} from "@/store/index.ts";
import {storeToRefs} from "pinia";
import useSlideHandler from "@/hooks/useSlideHandler"
import {showToast} from "@/utils/showToast";
import {useToast} from "primevue/usetoast";

const props = defineProps({
  parentFolder: {
    type: String,
    required: true
  },
  space: {
    type: String,
    required: false,
    default: null
  },
  uploadDirectoryId: {
    type: String,
    required: false,
    default: null
  },
  maxFiles: {
    type: Number,
    required: false,
    default: null
  },
  options: {
    type: Object,
    default: () => ({})
  },
  replaceFileId: {
    type: Number,
    required: false,
    default: null
  },
  breadcrumbItems: {
    type: Array,
    required: false
  },
  uploadTarget: {
    type: Object
  }
})


const slidesStore = useSlidesStore()
const {valueModelId, slideIndex, author, created, updated, theme} = storeToRefs(slidesStore)
const mainStore = useMainStore()

const {slideLibraryActive} = storeToRefs(mainStore)
const {selectedSlides} = useSlideHandler()
const toast = useToast()

const fileUploaded = ref(false)
const fileUploadError = ref(false)
const emit = defineEmits(['file-uploaded', 'update-upload-target']);
const concatBreadcrumbs = () => {
  const names = props.breadcrumbItems.filter(item => item.name !== 'Home').map(item => item.name);
  if (names.length === 0) {
    return '';
  }
  return '/' + names.join('/')
}


const cancelSaveAs = () => {
  emit('update-upload-target', null)
}

const saveFiles = async () => {
  const fileObj = {
    slides: selectedSlides.value,
    author: author.value,
    created: created.value,
    updated: updated.value,
    theme: theme.value,
    slideIndex: slideIndex.value
  }
  const signedUrl = await getSignedURL('main', props.uploadDirectoryId, concatBreadcrumbs())
  const xhr = new XMLHttpRequest();
  xhr.open('PUT', signedUrl);

  xhr.setRequestHeader('Content-Type', 'application/json');
  try {
    await new Promise((resolve, reject) => {
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {

          reject(new Error('Failed to upload'));
          showToast('error', 'Error', 'Failed to overwrite file', toast);
        }
      };
      xhr.onerror = () => reject(new Error('Failed to upload'));
      const jsonString = JSON.stringify(fileObj)
      xhr.send(jsonString);
      fileUploaded.value = true
      emit('update-upload-target')
      showToast('success', 'Success', 'File overwrite successful', toast);
      emit('file-uploaded')
    });
  } catch (error) {
    fileUploadError.value = true
    emit('update-upload-target')
    showToast('error', 'Error', 'An error occurred while overwriting the file', toast);
    console.error('Error:', error);
  }
};


const getSignedURL = async (space, currentFolder, breadcrumbItems) => {
  const url = `${import.meta.env.VITE_APP_ENV_SERVER}/lpi-service/job-service/create-signed-url-and-job`;
  const data = {
    eventType: 'file-service-upload',
    filePath: props.uploadTarget.title,
    contentType: 'application/json',
    jobDetails: {
      space: space,
      currentFolder: currentFolder,
      s3Prefix: breadcrumbItems,
      filePath: props.uploadTarget.title,
      vmId: slideLibraryActive.value ? "null" : valueModelId.value,
      thumbnailId: valueModelId.value,
    }
  };

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      console.error("Failed to send data:", response.statusText);
    }

    const parsedResponse = await response.json();
    return parsedResponse.putEndpoint;
  } catch (error) {
    console.error("Error sending data:", error);
    throw new Error('/');
  }
}


</script>

<style scoped>
:deep(.p-fileupload-buttonbar) {
  display: none;
}

:deep(.fileUploader) {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}


:deep(.icon-upload) {
  color: #94a3b8;
}

:deep(.icon-upload-border) {
  border: 1px solid #94a3b8;
  border-radius: 50%;
  padding: 20px;
}


.file-name {
  margin-bottom: 10px;
}

</style>
